import { AsyncPipe, DOCUMENT, NgTemplateOutlet } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  Inject,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  Version,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from "@angular/router";
import { BRANCHNAME, GITSHASHORT, SHOW_TALENT_POOL_UI, UserPermission } from "@ankaadia/ankaadia-shared";
import { APP_VERSION, translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { MessagesReception } from "@ankaadia/graphql";
import {
  EmptystateComponent,
  SidenavComponent,
  SidenavContentComponent,
  SidenavHeaderComponent,
  SidenavItemComponent,
  SidenavPanelComponent,
} from "@ankaadia/ui";
import { clone, isNil } from "lodash";
import { derivedAsync } from "ngxtension/derived-async";
import { ConfirmationService as OldConfirmationService } from "primeng-v17/api";
import { DialogService as OldDialogService } from "primeng-v17/dynamicdialog";
import { ConfirmationService, PrimeIcons } from "primeng/api";
import { Avatar } from "primeng/avatar";
import { ConfirmDialog } from "primeng/confirmdialog";
import { Dialog } from "primeng/dialog";
import { Divider } from "primeng/divider";
import { DialogService } from "primeng/dynamicdialog";
import { ToggleSwitch } from "primeng/toggleswitch";
import { Tooltip } from "primeng/tooltip";
import { BehaviorSubject, map, Observable, of, startWith, switchMap } from "rxjs";
import { environment } from "../../../environments/environment";
import { ActivityService } from "../../features/activity/activity.service";
import { NewUiCandidateProfileImageComponent } from "../../features/candidate-profile/candidate-profile-image/new-ui-candidate-profile-image.component";
import { CandidateFlexibleEditComponent } from "../../features/candidates/candidate-flexible-edit/candidate-flexible-edit.component";
import { CandidateFlexibleEditService } from "../../features/candidates/candidate-flexible-edit/candidate-flexible-edit.service";
import { NewUiCandidateFlexibleEditComponent } from "../../features/candidates/candidate-flexible-edit/new-ui-candidate-flexible-edit.component";
import { ChatConfigurationService } from "../../features/chat/chat-configuration.service";
import { ChatPreviewService } from "../../features/chat/chat-preview.service";
import { ChatWidgetDialogService } from "../../features/chat/chat-widget-dialog/chat-widget-dialog.service";
import { NewUiChatWidgetDialogComponent } from "../../features/chat/chat-widget-dialog/new-ui-chat-widget-dialog.component";
import { NotificationsComponent } from "../../features/notifications/notifications.component";
import { OnlineApplicationPermissions } from "../../features/online-application/online-application-constants";
import { NewUiOrganizationSwitchComponent } from "../../features/organization-switch/new-ui-organization-switch.component";
import { OrganizationSwitchService } from "../../features/organization-switch/organization-switch.service";
import { OrganizationsService } from "../../features/organizations/organizations.service";
import { TaskInboxService } from "../../features/process/task-inbox/task-inbox.service";
import { TalentPoolConfigService } from "../../features/talent-pool-config/talent-pool-config.service";
import { TalentPoolRoutesPermissions } from "../../features/talent-pool/talent-pool.constants";
import { NewUiTranslationProposalButtonComponent } from "../../features/translationProposal/translationProposalButton/new-ui-translationProposalButton.component";
import { UserConsentService } from "../../features/user-consent/user-consent.service";
import { UserEnvironmentService } from "../../features/user-environment/user-environment.service";
import { NewUiUserFeedbackComponent } from "../../features/user-feedback/new-ui-user-feedback.component";
import { UserFeedbackComponent } from "../../features/user-feedback/user-feedback.component";
import { NewUiApplicationLanguageComponent } from "../../features/users/application-language/new-ui-application-language.component";
import { NewUiCommunicationLanguageComponent } from "../../features/users/communication-language/new-ui-communication-language.component";
import { UsersService } from "../../features/users/users.service";
import { OrganizationFactoryService } from "../../organization-specific/organization-specific-factory.service";
import { NewUiDarkModeSwitchComponent } from "../../shared/dark-mode-switch/new-ui-dark-mode-switch.component";
import { LicensedDirective } from "../../shared/directives/licensed.directive";
import { PermissionDirective } from "../../shared/directives/permission.directive";
import { MyAccountService } from "../../shared/services/myAccount.service";
import { SettingsService } from "../../shared/services/settings.service";
import { TranslateDirective } from "../../shared/transloco/translate.directive";
import {
  NewUiViewModeSwitchComponent,
  ViewModeOption,
} from "../../shared/view-mode-switch/new-ui-view-mode-switch.component";
import { CustomMenuItem, IndexedMenuItem } from "../menu/menu.model";
import { SidebarComponent } from "../sidebar/sidebar.component";

@Component({
  selector: "new-ui-main",
  templateUrl: "./new-ui-main.component.html",
  imports: [
    RouterOutlet,
    AsyncPipe,
    ConfirmDialog,
    SidenavComponent,
    SidenavPanelComponent,
    SidenavHeaderComponent,
    SidenavContentComponent,
    SidenavItemComponent,
    RouterLink,
    TranslateDirective,
    PermissionDirective,
    LicensedDirective,
    Avatar,
    Divider,
    NewUiUserFeedbackComponent,
    Dialog,
    EmptystateComponent,
    NewUiCommunicationLanguageComponent,
    NewUiApplicationLanguageComponent,
    NewUiOrganizationSwitchComponent,
    NewUiDarkModeSwitchComponent,
    ToggleSwitch,
    FormsModule,
    NewUiTranslationProposalButtonComponent,
    NewUiChatWidgetDialogComponent,
    Tooltip,
    NgTemplateOutlet,
    NewUiViewModeSwitchComponent,
    NewUiCandidateProfileImageComponent,
  ],
})
export class NewUiMainComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly organizationService = inject(OrganizationsService);
  private readonly chatConfigurationService = inject(ChatConfigurationService);
  private readonly talentPoolService = inject(TalentPoolConfigService);

  protected readonly classicAnkaadiaConfirmationKey = "classicAnkaadiaConfirmationKey";
  protected readonly OnlineApplicationPermissions = OnlineApplicationPermissions;

  readonly UserPermission = UserPermission;
  readonly userDisplayName = this.settings.userDisplayName;
  readonly userEmail = this.settings.userEmail;
  readonly isCandidate = this.settings.isCandidate;
  readonly isLicensed = this.settings.isLicensed;
  readonly userOrCandidateId = this.settings.userOrCandidateId;
  readonly organizationId = this.settings.organizationId;
  readonly hasLogo = this.settings.hasLogo;
  readonly userGaveConsent$ = this.consentService.userGaveConsent$;
  readonly switchableOrganizationCount = this.settings.switchableOrganizationCount;
  readonly isProcessActionEnabled = this.settings.isProcessActionEnabled;
  readonly isMasterOrganization = this.settings.isMasterOrganization;
  readonly menu = this.settings.menu;
  readonly isEducationModuleVisibleForCandidate = this.settings.educationModuleVisible;
  readonly inboxCount$ = this.inboxService.counter;
  readonly unreadMessagesCount$ = this.chatPreviewService.getUnreadMessagesCount();

  readonly organizationLogo$ = this.organizationService.getOwnOrganizationLogoDownloadURL();
  readonly creatorOrganizationLogo$ = this.organizationService.getOrganizationLogoDownloadURL(
    this.settings.creatorOrganizationId
  );

  protected readonly version = computed(
    () => this.appVersion.full + (BRANCHNAME.startsWith("production") ? "" : ` (${BRANCHNAME}/${GITSHASHORT})`)
  );

  readonly talentPoolVisibility = derivedAsync(() => this.getTalentPoolVisibilityInclUserPermission());

  protected readonly image = toSignal(
    this.organizationLogo().pipe(
      switchMap((logo) => {
        if (isNil(logo)) {
          return of("assets/layout/images/logo.png");
        }

        return new Observable<string>((observer) => {
          const img = new Image();
          img.onload = (): void => {
            observer.next(logo);
            observer.complete();
          };
          img.onerror = (): void => {
            observer.next("assets/layout/images/logo.png");
            observer.complete();
          };
          img.src = logo;
        });
      })
    )
  );

  private organizationLogo(): Observable<string> {
    const { isCreatorMasterOrganization, isLicensed, creatorOrganizationName } = this.settings;
    const useCreatorLogo = isCreatorMasterOrganization && !isLicensed && creatorOrganizationName;
    return useCreatorLogo ? this.creatorOrganizationLogo$ : this.organizationLogo$;
  }

  protected getOrganizationNameForHeader(): string {
    return `${this.settings.organizationName} (${this.settings.organizationCode})`;
  }

  protected getOrganizationCode(): string {
    return this.settings.organizationCode;
  }

  protected getParentOrganizationNameForHeader(): string {
    return translateSafely("organization.providedBy", {
      creatorOrganizationName: this.settings.creatorOrganizationName,
    });
  }

  readonly helpUrl$ = this.getHelpUrl();

  private readonly myOtherOrganizationSub = new BehaviorSubject<string>(null);
  readonly myOtherOrganizations$ = this.myOtherOrganizationSub.pipe(
    switchMap((x) => this.organizationSwitchService.list(x))
  );

  protected readonly ViewModeOption = ViewModeOption;

  readonly notificationCount$ = this.activityService.notificationCount;
  readonly isChatEnabled$ = this.chatConfigurationService.watchIsChatEnabled();

  protected readonly enabledFeatures = {
    talentPool: SHOW_TALENT_POOL_UI,
  };

  private inboxTimer: number;
  private notificationTimer: number;

  activeTabIndex: number;
  sidebarActive: boolean;
  topbarMenuActive: boolean;
  sidebarClick: boolean;
  topbarItemClick: boolean;
  activeTopbarItem: any;
  documentClickListener: any;
  showResetSuccessDialog: boolean;
  showMessageReceptionSuccessDialog: boolean;
  showLanguageDialog: boolean;
  showOrganizationSwitchDialog: boolean;
  showDeleteDataSuccessDialog: boolean;
  viewMode: ViewModeOption;

  @ViewChild(environment.newUI ? NewUiUserFeedbackComponent : UserFeedbackComponent)
  userFeedback: UserFeedbackComponent | NewUiUserFeedbackComponent;

  @ViewChild(NotificationsComponent)
  notifications: NotificationsComponent;

  @ViewChild("sidebar")
  sidebar: SidebarComponent;

  @ViewChild("candidateEditDialogProxy", { read: ElementRef })
  protected candidateEditDialogProxy: ElementRef;

  protected messagesReception: MessagesReception;

  constructor(
    @Inject(APP_VERSION) private readonly appVersion: Version,
    private readonly renderer: Renderer2,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    protected readonly settings: SettingsService,
    private readonly inboxService: TaskInboxService,
    private readonly activityService: ActivityService,
    private readonly candidateFlexibleEditService: CandidateFlexibleEditService,
    @Inject(environment.newUI ? DialogService : OldDialogService)
    private readonly dialogService: OldDialogService | DialogService,
    @Inject(environment.newUI ? ConfirmationService : OldConfirmationService)
    private readonly confirmationService: OldConfirmationService | ConfirmationService,
    private readonly destroyRef: DestroyRef,
    private readonly specificsFactory: OrganizationFactoryService,
    @Inject(DOCUMENT) private readonly documentRef: Document,
    private readonly consentService: UserConsentService,
    private readonly userService: UsersService,
    private readonly userEnvironmentService: UserEnvironmentService,
    private readonly myAccountService: MyAccountService,
    private readonly organizationSwitchService: OrganizationSwitchService,
    private readonly chatWidgetDialogService: ChatWidgetDialogService,
    private readonly chatPreviewService: ChatPreviewService,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit(): void {
    if (this.settings.startUrl) {
      void this.router.navigate([this.settings.startUrl]);
    }

    if (environment.newUI) {
      this.userService.messagesReceptionChanged
        .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.fetchCurrentMsgReception());
    }

    const DELAY_2_SECONDS = 2 * 1000;
    const EVERY_10_MINUTES = 10 * 60 * 1000;
    //const EVERY_30_MINUTES = 30 * 60 * 1000;
    const EVERY_60_MINUTES = 60 * 60 * 1000;

    // Run intervals outside Angular zone to prevent triggering change detection on every tick
    this.ngZone.runOutsideAngular(() => {
      window.setTimeout(() => this.inboxService.updateInboxCount(), DELAY_2_SECONDS);
      this.inboxTimer = window.setInterval(() => {
        this.inboxService.updateInboxCount();
      }, EVERY_60_MINUTES);

      if (!this.settings.isCandidate && this.settings.hasUserPermission) {
        window.setTimeout(() => this.activityService.updateNotificationCount(), DELAY_2_SECONDS);

        this.notificationTimer = window.setInterval(
          () => this.activityService.updateNotificationCount(),
          EVERY_10_MINUTES
        );
      }
    });
  }

  ngAfterViewInit(): void {
    this.documentClickListener = this.renderer.listen("body", "click", () => {
      if (!this.topbarItemClick) {
        this.activeTopbarItem = null;
        this.topbarMenuActive = false;
      }

      if (!this.sidebarClick) {
        this.sidebarActive = false;
      }
      this.topbarItemClick = false;
      this.sidebarClick = false;
    });

    this.candidateFlexibleEditService
      .initOpenCandidateListener()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((candidate) => {
        if (environment.newUI) {
          (<DialogService>this.dialogService)
            .open(NewUiCandidateFlexibleEditComponent, {
              header: translateSafely("candidate.edit.title"),
              data: candidate,
              focusOnShow: false,
              modal: true,
              appendTo: "body",
              closable: true,
              draggable: true,
              width: "90vw",
              height: "90vh",
              contentStyle: { overflow: "hidden" },
            })
            .onClose.subscribe(() => this.candidateFlexibleEditService.close());
        } else {
          (<OldDialogService>this.dialogService)
            .open(CandidateFlexibleEditComponent, {
              closeOnEscape: false,
              dismissableMask: false,
              resizable: true,
              closable: true,
              draggable: true,
              header: translateSafely("candidate.edit.title"),
              data: candidate,
              maximizable: true,
              width: "80%",
              height: "80%",
              appendTo: this.candidateEditDialogProxy.nativeElement,
            })
            .onClose.subscribe(() => this.candidateFlexibleEditService.close());
        }
      });
  }

  openSidebar(index: number): void {
    this.sidebarClick = true;
    this.sidebarActive = true;
    this.activeTabIndex = index;
    this.cd.detectChanges();
  }

  openFeedback(): void {
    this.userFeedback.open();
  }

  openNotifications(): void {
    this.notifications.open();
  }

  onTabClick(event: Event, index: number): void {
    if (this.settings.isCandidate) {
      const item = this.sidebar.sections.find((s) => s.index === index)?.items[0];
      if (item) this.navigateTo(item, index);
    } else {
      if (this.activeTabIndex === index) {
        this.sidebarActive = !this.sidebarActive;
      } else {
        this.activeTabIndex = index;
        this.sidebarActive = true;
      }
    }

    event.preventDefault();
  }

  onSidebarNavigation(item: IndexedMenuItem): void {
    this.navigateTo(item, item.index);
    this.sidebarActive = false;
  }

  navigateTo(item: CustomMenuItem, index: number): void {
    this.activeTabIndex = index;
    void this.router.navigate(item.routerLink, { relativeTo: this.route });
  }

  closeSidebar(event: Event): void {
    this.sidebarActive = false;
    event?.preventDefault();
  }

  onSidebarClick(): void {
    this.sidebarClick = true;
  }

  onTopbarMenuButtonClick(event: Event): void {
    this.topbarItemClick = true;
    this.topbarMenuActive = !this.topbarMenuActive;

    event.preventDefault();
  }

  onTopbarItemClick(event: Event, item: any): void {
    this.topbarItemClick = true;

    if (this.activeTopbarItem === item) {
      this.activeTopbarItem = null;
    } else {
      this.activeTopbarItem = item;
    }

    event.preventDefault();
  }

  onTopbarSubItemClick(event: Event): void {
    event.preventDefault();
  }

  ngOnDestroy(): void {
    if (this.documentClickListener) {
      this.documentClickListener();
    }
    clearInterval(this.inboxTimer);
    clearInterval(this.notificationTimer);
  }

  openWidget(): void {
    this.chatWidgetDialogService.toggle();
  }

  goToUrl(url: string): void {
    this.documentRef.defaultView.open(url, "_blank");
  }

  goHome(): void {
    this.settings.navigateToMain();
  }

  copyUserEmailToClipboard(): void {
    void navigator.clipboard.writeText(this.userEmail);
  }

  resetPassword(event: Event): void {
    if (environment.newUI) {
      event.stopPropagation();

      this.confirmationService.confirm({
        target: event.target,
        header: translateSafely("password.reset"),
        message: translateSafely("password.confirmReset"),
        acceptLabel: translateSafely("password.reset"),
        acceptButtonProps: { severity: "success" },
        accept: () => {
          this.myAccountService.resetPassword().subscribe(() => {
            this.showResetSuccessDialog = true;
          });
        },
      });
    }
  }

  deleteCandidateData(event: Event): void {
    if (environment.newUI) {
      event.stopPropagation();

      this.confirmationService.confirm({
        target: event.target,
        message: translateSafely("candidateDeletion.confirmOne"),
        acceptButtonProps: { severity: "danger" },
        accept: () => {
          this.myAccountService.deleteCandidateData().subscribe(() => {
            this.showDeleteDataSuccessDialog = true;
          });
        },
      });
    }
  }

  logout(event: Event): void {
    if (environment.newUI) {
      event.stopPropagation();

      this.confirmationService.confirm({
        target: event.target,
        header: translateSafely("logout.title"),
        message: translateSafely("logout.confirm"),
        acceptLabel: translateSafely("logout.title"),
        acceptIcon: PrimeIcons.SIGN_OUT,
        acceptButtonProps: { severity: "danger" },
        accept: () => {
          void this.router.navigate(["/logout"]);
        },
      });
    }
  }

  toggleMessagesReception(change: { value: boolean; target: EventTarget }): void {
    if (environment.newUI) {
      this.confirmationService.confirm({
        target: change.target,
        message: change.value
          ? translateSafely("messagesReception.confirmationYes")
          : translateSafely("messagesReception.confirmationNo"),
        acceptButtonProps: { severity: "success" },
        accept: () => {
          this.userService.toggleMessagesReception(this.settings.isCandidate).subscribe(() => {
            this.showMessageReceptionSuccessDialog = true;
          });
        },
        reject: () => this.userService.messagesReceptionChanged.next(),
      });
    }
  }

  loadOrganizations(search: string): void {
    this.myOtherOrganizationSub.next(search);
  }

  switchOrganization(organizationId: string): void {
    this.settings.switchOrganization(organizationId);
  }

  openKpiPage(event: Event): void {
    event.stopPropagation();
    void this.router.navigate(["/kpi"]);
  }

  protected openClassicAnkaadiaUi(event: MouseEvent): void {
    this.userEnvironmentService.switchToAlternativeEnvironment(this.classicAnkaadiaConfirmationKey, event);
  }

  private getHelpUrl(): Observable<string> {
    return this.specificsFactory
      .getOrganizationSpecifics(this.settings.organizationId)
      .pipe(map((specifics) => specifics.getHelpUrl()));
  }

  private fetchCurrentMsgReception(): void {
    this.userService.getMessagesReception(this.settings.isCandidate).subscribe((val) => {
      this.messagesReception = clone(val);
    });
  }

  private getTalentPoolVisibilityInclUserPermission(): Observable<{
    contributorPortal: boolean;
    userPortal: boolean;
    talentPoolManagement: boolean;
  }> {
    return this.getTalentPoolVisibility().pipe(
      map((x) => ({
        userPortal: (x?.userPortal && this.settings.hasAnyPermission(TalentPoolRoutesPermissions.view)) ?? false,
        contributorPortal:
          (x?.contributorPortal && this.settings.hasAnyPermission(TalentPoolRoutesPermissions.requests)) ?? false,
        talentPoolManagement:
          (x?.talentPoolManagement && this.settings.hasAnyPermission(TalentPoolRoutesPermissions.edit)) ?? false,
      }))
    );
  }

  private getTalentPoolVisibility(): Observable<{
    contributorPortal: boolean;
    userPortal: boolean;
    talentPoolManagement: boolean;
  }> {
    if (!SHOW_TALENT_POOL_UI || this.isCandidate) {
      return of({
        contributorPortal: false,
        userPortal: false,
        talentPoolManagement: false,
      });
    }

    if (this.isMasterOrganization || this.settings.hasModule("talentPool")) {
      return of({
        contributorPortal: true,
        userPortal: true,
        talentPoolManagement: true,
      });
    }

    return this.talentPoolService.watchParticipationInfo().pipe(
      map((value) => ({
        contributorPortal: value.isContributorInPool || value.canContributeToAnkaadiaPool,
        userPortal: value.isUserInPool,
        talentPoolManagement: this.settings.hasModule("talentPool") || value.website,
      }))
    );
  }
}
